import type { ArrowData, Cell, Direction, Level, ShapeName } from './types';
import { CHESS_SHAPES, chessCells, isChessShape } from './chessShapes';
import { THEMED_SHAPES, isThemedShape, themedCells } from './themedShapes';
import { openingLevel } from './openingLevels';

export const GENERATOR_VERSION = 7;
export const DESIGN_GENERATOR_VERSION = 6;
const DIRS: Record<Direction, Cell> = { UP: { x: 0, y: -1 }, DOWN: { x: 0, y: 1 }, LEFT: { x: -1, y: 0 }, RIGHT: { x: 1, y: 0 } };
const DIRECTION_NAMES: Direction[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];
const key = (p: Cell) => `${p.x},${p.y}`;
const add = (a: Cell, b: Cell): Cell => ({ x: a.x + b.x, y: a.y + b.y });
const equals = (a: Cell, b: Cell) => a.x === b.x && a.y === b.y;

export function randomSource(seed: number): () => number {
  let x = seed >>> 0;
  return () => {
    x += 0x6D2B79F5;
    let t = x;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffle<T>(items: T[], rand: () => number): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
// Keep the established v2/v3 rotation fixed so old seeded attempts still replay exactly.
export const CLASSIC_SHAPES: ShapeName[] = ['rectangle', 'square', 'circle', 'diamond', 'cross', 'star', 'l-shape', 'custom', 'hollow'];
export const SHAPES: ShapeName[] = [...CLASSIC_SHAPES, ...CHESS_SHAPES, ...THEMED_SHAPES];

export function activeCells(width: number, height: number, shape: ShapeName): Cell[] {
  if (isChessShape(shape)) return chessCells(width, height, shape);
  if (isThemedShape(shape)) return themedCells(width, height, shape);
  const out: Cell[] = [];
  const cx = (width - 1) / 2, cy = (height - 1) / 2;
  const rx = Math.max(1, cx), ry = Math.max(1, cy);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const nx = (x - cx) / rx, ny = (y - cy) / ry;
    let inside = true;
    switch (shape) {
      case 'circle': inside = nx * nx + ny * ny <= 1.09; break;
      case 'diamond': inside = Math.abs(nx) + Math.abs(ny) <= 1.12; break;
      case 'cross': inside = Math.abs(nx) <= 0.35 || Math.abs(ny) <= 0.35; break;
      case 'star': {
        const angle = Math.atan2(ny, nx) + Math.PI / 2;
        const theta = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const sector = theta / (Math.PI / 5);
        const fraction = sector - Math.floor(sector);
        const outer = Math.floor(sector) % 2 === 0;
        const limit = outer ? 1 - 0.45 * fraction : 0.55 + 0.45 * fraction;
        inside = Math.hypot(nx, ny) <= limit * 1.12;
        break;
      }
      case 'l-shape': inside = x <= Math.floor(width * 0.36) || y >= Math.floor(height * 0.59); break;
      case 'custom': inside = (x <= Math.floor(width * 0.7) && y <= Math.floor(height * 0.55)) ||
        (x >= Math.floor(width * 0.32) && y >= Math.floor(height * 0.35)); break;
      case 'hollow': inside = !(Math.abs(nx) < 0.38 && Math.abs(ny) < 0.38); break;
      case 'rectangle':
      case 'square': break;
    }
    if (inside) out.push({ x, y });
  }
  return out;
}

type ExitRule = 'board-edge' | 'shape-edge';

// Only used to reconstruct or validate historical content before migration.
function canSlideThroughShape(arrow: ArrowData, active: Set<string>, occupied: Set<string>): boolean {
  const direction = DIRS[arrow.direction];
  const tip = arrow.cells[arrow.cells.length - 1];
  let pos = add(tip!, direction);
  while (active.has(key(pos))) {
    if (occupied.has(key(pos))) return false;
    pos = add(pos, direction);
  }
  return true;
}

/** Every occupied cell ahead blocks, including across holes and disconnected parts. */
export function canSlide(arrow: ArrowData, _active: Set<string>, occupied: Set<string>): boolean {
  const tip = arrow.cells[arrow.cells.length - 1];
  const direction = DIRS[arrow.direction];
  for (const cell of occupied) {
    const [x, y] = cell.split(',').map(Number);
    if (direction.x !== 0 ? y === tip!.y && (x! - tip!.x) * direction.x > 0 :
      x === tip!.x && (y! - tip!.y) * direction.y > 0) return false;
  }
  return true;
}

export function isSolvable(arrows: ArrowData[], active: Cell[], rule: ExitRule = 'board-edge'): boolean {
  const activeKeys = new Set(active.map(key));
  const remaining = new Map(arrows.map(a => [a.id, a]));
  while (remaining.size) {
    const occupied = new Set([...remaining.values()].flatMap(a => a.cells.map(key)));
    const removable = [...remaining.values()].find(a => (rule === 'shape-edge' ? canSlideThroughShape : canSlide)(a, activeKeys, occupied));
    if (!removable) return false;
    remaining.delete(removable.id);
  }
  return true;
}

export function validateLevel(level: Level, rule: ExitRule = 'board-edge'): string[] {
  const issues: string[] = [];
  const active = new Set(level.active.map(key));
  const taken = new Set<string>();
  for (const a of level.arrows) {
    if (a.cells.length < 2) issues.push(`${a.id}: arrow too short`);
    const visited = new Set<string>();
    for (let i = 0; i < a.cells.length; i++) {
      const cell = a.cells[i];
      if (!active.has(key(cell!))) issues.push(`${a.id}: out of shape ${key(cell!)}`);
      if (taken.has(key(cell!))) issues.push(`${a.id}: occupied twice ${key(cell!)}`);
      if (visited.has(key(cell!))) issues.push(`${a.id}: self overlap ${key(cell!)}`);
      if (i > 0 && Math.abs(cell!.x - a.cells[i - 1]!.x) + Math.abs(cell!.y - a.cells[i - 1]!.y) !== 1) issues.push(`${a.id}: nonadjacent cells`);
      visited.add(key(cell!));
      taken.add(key(cell!));
    }
    if (a.cells.length >= 2) {
      const tip = a.cells[a.cells.length - 1], prior = a.cells[a.cells.length - 2];
      if (!equals({ x: tip!.x - prior!.x, y: tip!.y - prior!.y }, DIRS[a.direction])) issues.push(`${a.id}: kinked head`);
      if (!(rule === 'shape-edge' ? canSlideThroughShape : canSlide)(a, active, visited)) issues.push(`${a.id}: self blocked exit`);
    }
  }
  if (!level.arrows.length) issues.push('no arrows');
  if (!isSolvable(level.arrows, level.active, rule)) issues.push('level has deadlock');
  return issues;
}

/** Build in removal order: each tip faces space already cleared. Every accepted
 * path leaves no isolated cell behind, so the result can fill the entire mask. */
function fillBoard(active: Cell[], rand: () => number, maxLength: number, straightBias = .62, rule: ExitRule = 'board-edge'): ArrowData[] | null {
  const remaining = new Set(active.map(key));
  const activeSet = new Set(remaining);
  const maxX = Math.max(...active.map(c => c.x)), maxY = Math.max(...active.map(c => c.y));
  const inExitPath = (cell: Cell) => rule === 'shape-edge' ? activeSet.has(key(cell)) :
    cell.x >= 0 && cell.y >= 0 && cell.x <= maxX && cell.y <= maxY;
  const arrows: ArrowData[] = [];
  const neighbors = (cell: Cell) => DIRECTION_NAMES.map(d => add(cell, DIRS[d]));
  while (remaining.size) {
    let accepted: ArrowData | null = null;
    for (const tip of shuffle(active.filter(c => remaining.has(key(c))), rand)) {
      for (const direction of shuffle(DIRECTION_NAMES, rand)) {
        const forward = DIRS[direction];
        let exit = add(tip, forward);
        let blocked = false;
        while (inExitPath(exit)) {
          if (remaining.has(key(exit))) { blocked = true; break; }
          exit = add(exit, forward);
        }
        if (blocked) continue;
        const back = { x: tip.x - forward.x, y: tip.y - forward.y };
        if (!remaining.has(key(back))) continue;
        const path = [tip, back];
        const used = new Set(path.map(key));
        const target = 3 + Math.floor(rand() * (maxLength - 2));
        for (let step = 2; step < target; step++) {
          const last = path[path.length - 1], previous = path[path.length - 2];
          const options = shuffle(neighbors(last!), rand).filter(c => remaining.has(key(c)) && !used.has(key(c)));
          if (!options.length) break;
          // Longer straight stretches, interspersed with bends, read as a maze.
          const straight = options.find(c => c.x - last!.x === last!.x - previous!.x && c.y - last!.y === last!.y - previous!.y);
          const next = straight && rand() < straightBias ? straight : options[0];
          path.push(next!);
          used.add(key(next!));
        }
        // Back off a tail if the longer path would strand a one-cell fragment.
        while (path.length >= 2) {
          const stranded = active.some(c => remaining.has(key(c)) && !used.has(key(c)) &&
            !neighbors(c).some(n => remaining.has(key(n)) && !used.has(key(n))));
          if (!stranded) {
            accepted = { id: `a${arrows.length + 1}`, cells: [...path].reverse(), direction };
            break;
          }
          used.delete(key(path.pop()!));
        }
        if (accepted) break;
      }
      if (accepted) break;
    }
    if (!accepted) return null;
    arrows.push(accepted);
    for (const c of accepted.cells) remaining.delete(key(c));
  }
  return arrows;
}

function makeShape(levelNumber: number): ShapeName {
  if (levelNumber === 2) return 'square';
  if (levelNumber <= 5) return 'rectangle';
  return CLASSIC_SHAPES[(levelNumber - 1) % CLASSIC_SHAPES.length]!;
}
export function seedForLevel(level: number): number {
  return (Math.imul(level, 2654435761) ^ 0xA11CE123) >>> 0;
}

export function generateLevel(number: number, seed = seedForLevel(number), overrideShape?: ShapeName, version = GENERATOR_VERSION): Level {
  if (![2, 3, 5, GENERATOR_VERSION].includes(version)) throw new Error(`Unsupported generator version: ${version}`);
  if (version >= 7 && overrideShape === undefined) {
    const opening = openingLevel(number, seed, version);
    if (opening) {
      const issues = validateLevel(opening);
      if (issues.length) throw new Error(`Invalid opening level ${number}: ${issues.join(', ')}`);
      return opening;
    }
  }
  const rule: ExitRule = version < 5 ? 'shape-edge' : 'board-edge';
  const tier = Math.min(4, Math.floor((number - 1) / 20));
  const shape = overrideShape ?? makeShape(number);
  const large = version >= 3 && number > 2;
  const width = large ? 20 + tier * 2 : 12 + tier;
  const height = shape === 'rectangle' ? width + (large ? 8 : 4) : width;
  const active = activeCells(width, height, shape);
  let best: ArrowData[] | null = null;
  // Bounded, seeded restarts avoid pathological leftover regions. A successful
  // pass is a complete tiling, never a sparse partial puzzle.
  for (let round = 0; round < 64; round++) {
    best = fillBoard(active, randomSource((seed + round * 0x9E3779B9) >>> 0), large ? 25 + tier * 3 : 15 + tier * 2, .62, rule);
    if (best) break;
  }
  if (!best) throw new Error(`Could not tile level ${number}/${shape}`);
  // A short scan allowance plus roughly one second per arrow. Larger tiers
  // get a little more thinking time, without returning to multi-minute rounds.
  const timeLimit = Math.min(large ? 135 : 65, Math.max(30, Math.ceil((12 + best.length + tier * 2 + (large ? 8 : 0)) / 5) * 5));
  const level: Level = {
    id: `level-${number}`, number, seed, generatorVersion: version,
    shape, width, height, active, arrows: best, difficulty: tier + 1, timeLimit
  };
  const issues = validateLevel(level, rule);
  if (issues.length) throw new Error(`Generator failed ${number}/${shape}: ${issues.join(', ')}`);
  return level;
}

export function generateDaily(date: string, version = GENERATOR_VERSION): Level {
  const seed = [...date].reduce((sum, char) => Math.imul(sum ^ char.charCodeAt(0), 16777619) >>> 0, 2166136261);
  const number = 38 + seed % 35;
  const base = generateLevel(number, seed, undefined, version);
  return { ...base, id: `daily-${date}`, number: 0, timeLimit: base.timeLimit };
}

export type LevelDesign = {
  number: number; seed: number; shape: ShapeName; width: number; height: number;
  difficulty: number; timeLimit: number; maxLength: number; winding: 'gentle' | 'balanced' | 'twisty';
};

/** Designer content is stored as an exact snapshot, separate from seeded v1–v3 progression. */
export function validateDesign(design: LevelDesign): void {
  for (const [name, value, min, max] of [
    ['Level', design.number, 1, 100], ['Seed', design.seed, 0, 4294967295],
    ['Width', design.width, 8, 32], ['Height', design.height, 8, 40],
    ['Difficulty', design.difficulty, 1, 5], ['Timer', design.timeLimit, 15, 300],
    ['Arrow length', design.maxLength, 3, 60]
  ] as const) {
    if (!Number.isInteger(value) || value < min || value > max) throw new Error(`${name} must be an integer from ${min} to ${max}.`);
  }
  if (!SHAPES.includes(design.shape)) throw new Error('Choose a supported shape.');
  if (!['gentle', 'balanced', 'twisty'].includes(design.winding)) throw new Error('Choose a supported winding style.');
  if (design.shape === 'square' && design.width !== design.height) throw new Error('Square boards need equal width and height.');
}

export function generateDesignedLevel(design: LevelDesign): Level {
  validateDesign(design);
  const active = activeCells(design.width, design.height, design.shape);
  const bias = { gentle: .85, balanced: .62, twisty: .25 }[design.winding];
  for (let round = 0; round < 64; round++) {
    const arrows = fillBoard(active, randomSource((design.seed + round * 0x9E3779B9) >>> 0), design.maxLength, bias);
    if (!arrows) continue;
    const level: Level = { id: `level-${design.number}`, number: design.number, seed: design.seed,
      generatorVersion: DESIGN_GENERATOR_VERSION, shape: design.shape, width: design.width, height: design.height,
      difficulty: design.difficulty, timeLimit: design.timeLimit, active, arrows };
    const issues = validateLevel(level);
    if (issues.length) throw new Error(issues.join(', '));
    return level;
  }
  throw new Error('Could not completely fill this shape. Try a different seed or larger dimensions.');
}

/** Keep compatible saved geometry. Retile incompatible old snapshots on their
 * exact mask so stricter blocking cannot strand an existing puzzle. */
export function upgradeExitRules(level: Level): Level {
  if (level.generatorVersion >= 5 || validateLevel(level).length === 0) return level;
  const legacyIssues = validateLevel(level, 'shape-edge');
  if (legacyIssues.length) throw new Error(`Cannot upgrade invalid puzzle: ${legacyIssues[0]}`);
  const maxLength = Math.max(3, ...level.arrows.map(arrow => arrow.cells.length));
  for (let round = 0; round < 64; round++) {
    const arrows = fillBoard(level.active, randomSource((level.seed + round * 0x9E3779B9) >>> 0), maxLength);
    if (!arrows) continue;
    const upgraded = { ...level, generatorVersion: level.generatorVersion === 4 ? DESIGN_GENERATOR_VERSION : GENERATOR_VERSION, arrows };
    const issues = validateLevel(upgraded);
    if (issues.length) throw new Error(issues[0]);
    return upgraded;
  }
  throw new Error('This saved board could not be updated. Generate it again in Level Studio.');
}
